
import {
  Adapter,
  Channel,
  IAdapter,
  IChannel,
  IChannelData,
  ICountryConfig,
  ICountryConfigResponse,
  ICurrency,
  IProsegurPay,
  IServicepointDetail,
  IServicepointDetailData,
  IThirdcode,
  IUpdateServicepoint,
} from "@/entities";
import { i18n } from "@/i18n";
import { IGenericResponse, IPageRequest, IPageResponse } from "@/services";
import { debounce } from "lodash";
import { extend, ValidationObserver } from "vee-validate";
import Multiselect from "vue-multiselect";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { SERVICEPOINT_TYPE } from "@/constans";
import ThirdcodeTable from "@/components/thirdcode/ThirdcodeTable.vue";
import LimitsTable from "@/components/limits/LimitsTable.vue";
import ProsegurPay from "@/components/prosegurpay/ProsegurPay.vue";
import UsersOnFlowApprove from "../approvals/UsersOnFlowApprove.vue";
import { IUserServicepointApproval } from "@/entities/servicepoint_approval";
import AsyncComputed from "vue-async-computed-decorator";

@Component({
  name: "servicepoint-editable-detail",
  components: {
    ValidationObserver,
    Multiselect,
    ThirdcodeTable,
    LimitsTable,
    ProsegurPay,
    UsersOnFlowApprove,
  },
  props: ["detail"],
})
export default class ServicepointEditableDetail extends Vue {
  @Prop() public detail!: IServicepointDetail;

  @Action("fetchChannelsPage") fetchChannelsPage!: ({
    pageRequest,
    text,
    countryId,
  }: {
    pageRequest: IPageRequest;
    text?: string;
    countryId?: number;
  }) => Promise<IPageResponse<IChannelData> | undefined>;
  @Action("createChannel") createChannel!: ({
    text,
    countryId,
  }: {
    text?: string;
    countryId?: number;
  }) => Promise<IGenericResponse<IChannelData> | undefined>;
  @Action("saveUpdateServicepoint") saveUpdateServicepoint!: (
    updateServicepoint: IUpdateServicepoint
  ) => Promise<IGenericResponse<IServicepointDetail> | undefined>;
  @Action("updateStatusServicepoint") updateStatusServicepoint!: (id: number) => Promise<void>;
  @Getter("getAssignableAdapters") assignableAdapters!: IAdapter[];
  @Getter("getCurrenciesPage") currenciesPage: IPageResponse<ICurrency> | undefined;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig;

  public type?: SERVICEPOINT_TYPE = SERVICEPOINT_TYPE.SOL;
  public oidSolInput?: string | null = null;
  public codeSolInput?: string | null = null;
  public idSmartcashInput?: string | null = null;
  public uniqueCodeInput?: string | null = null;
  public deviceIdInput?: string | null = null;
  public descriptionInput?: string | null = null;
  public addressInput?: string | null = null;
  public openChannelInput?: boolean | null = null;
  public accountDateInput?: boolean | null = null;
  public assignableAdaptersFinal: IAdapter[] = [];
  public adaptersSelect: IAdapter[] = [];
  public currenciesSelect: ICurrency[] = [];
  public channelsSelect: IChannel[] = [];
  public ssIdInput?: string | null = null;
  public passwordInput?: string | null = null;
  public agilePickup = false;
  public enabledFlowApproval = false;
  public econoCash = false;
  public prosegurPayInput: IProsegurPay | null = null;
  public enabledReadQr = false;
  public channels: IChannel[] = [];
  public usersApprovalList: IUserServicepointApproval[] = [];

  mainCurrency?: string;

  checkboxText = i18n.t("label.sol.upper");

  public thirdCodes?: IThirdcode[] = [];

  constructor() {
    super();
  }

  public initializeFormData(): void {
    this.type = this.detail?.type;
    this.oidSolInput = this.detail?.oidSol;
    this.codeSolInput = this.detail?.codeSol;
    this.idSmartcashInput = this.detail?.idSmartcash;
    this.uniqueCodeInput = this.detail?.uniqueCode;
    this.deviceIdInput = this.detail?.deviceId;
    this.descriptionInput = this.detail?.description;
    this.addressInput = this.detail?.address;
    this.openChannelInput = this.detail?.openChannel;
    this.accountDateInput = this.detail?.accountDate;
    this.adaptersSelect = this.detail?.adapters;
    this.currenciesSelect = this.detail?.currencies;
    this.channelsSelect = this.detail?.channels;
    this.mainCurrency = this.detail.subclient?.client?.country?.mainCurrency?.code;
    this.ssIdInput = this.detail?.ssId;
    this.passwordInput = this.detail?.password;
    this.econoCash = this.detail?.econoCash || false;
    this.thirdCodes = this.detail?.thirdcodes || [];
    this.agilePickup = this.detail?.agilePickup || false;
    this.prosegurPayInput = this.detail?.prosegurPay || null;
    this.enabledReadQr = this.detail?.enabledReadQr || false;
    this.enabledFlowApproval = this.detail?.enabledFlowApproval || false;
    this.usersApprovalList = this.detail?.userApprovals || [];

    this.checkboxText =
      this.type === SERVICEPOINT_TYPE.SOL
        ? i18n.t("label.sol.upper")
        : i18n.t("label.smartcash.upper");

    this.checkAdapters();
  }

  get countryId(): number | undefined {
    return this.detail?.subclient?.client?.country?.id;
  }

  @AsyncComputed()
  async config(): Promise<ICountryConfigResponse | undefined> {
    if (this.detail.subclient?.client?.country?.code) {
      return this.$services.country.getCountryConfigByCode(
        this.detail.subclient.client.country.code
      );
    }
    return {};
  }

  get showAgilePickupOption(): boolean {
    return this.adaptersSelect.findIndex((adapter) => adapter.code === "pickup") !== -1;
  }
  get isDepositModule(): boolean {
    return this.adaptersSelect.findIndex((adapter) => adapter.code === "deposit") !== -1;
  }
  get isRVModule(): boolean {
    return this.adaptersSelect.findIndex((adapter) => adapter.code === "rv") !== -1;
  }

  get isProsegurPayModule(): boolean {
    return this.adaptersSelect.findIndex((adapter) => adapter.code === "prosegurpay") !== -1;
  }

  get typeSOL(): boolean {
    return this.type === SERVICEPOINT_TYPE.SOL;
  }

  set typeSOL(value: boolean) {
    this.type = value ? SERVICEPOINT_TYPE.SOL : SERVICEPOINT_TYPE.CASHTODAY;
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  async save(): Promise<void> {
    try {
      const userApprovals = this.getUsersApprovalData();
      const updateServicepoint: IUpdateServicepoint = {
        id: this.detail?.id,
        type: this.type,
        oidSol: this.oidSolInput || "",
        codeSol: this.codeSolInput || "",
        idSmartcash: this.idSmartcashInput || undefined,
        uniqueCode: this.uniqueCodeInput || "",
        deviceId: this.deviceIdInput || undefined,
        description: this.descriptionInput || "",
        address: this.addressInput || "",
        openChannel: this.openChannelInput || false,
        accountDate: this.accountDateInput || false,
        active: this.detail?.active || false,
        subclientId: this.detail?.subclient?.id || this.detail?.subclientId || 0,
        ssId: this.ssIdInput || "",
        password: this.passwordInput || "",
        adapters: this.adaptersSelect.map((adapter) => ({
          id: adapter.id || 0,
        })),
        channels: this.channelsSelect.map((channel) => ({
          id: channel.id || 0,
        })),
        currencies: this.currenciesSelect.map((currency) => ({
          id: currency.id || 0,
        })),
        thirdcodes: this.detail.thirdcodes,
        limits: this.detail.limits,
        agilePickup: this.agilePickup,
        econoCash: this.econoCash,
        prosegurPay: this.getProsegurPayData(),
        enabledReadQr: this.enabledReadQr,
        enabledFlowApproval: this.enabledFlowApproval,
        userApprovals: userApprovals,
        countryId: this.detail.subclient?.client?.country?.id,
      };

      const res = await this.saveUpdateServicepoint(updateServicepoint);

      if (res && res.status === 1) {
        this.makeToast("success", this.$t(res.message) as string);
      }

      this.$emit("fetch-servicepoint-required");
    } catch (error) {
      this.$emit("fetch-servicepoint-required");
    }
  }

  getUsersApprovalData(): number[] {
    if (this.enabledFlowApproval) {
      return this.usersApprovalList.map((user) => user.id);
    } else {
      return this.keepInformationUsersApprovalData();
    }
  }

  keepInformationUsersApprovalData(): number[] {
    if (this.detail?.userApprovals) {
      return this.detail.userApprovals.map((user) => user.id);
    } else {
      return [];
    }
  }

  getProsegurPayData(): IProsegurPay {
    if (this.isProsegurPayModule && this.prosegurPayInput) {
      const data = this.prosegurPayInput;
      return {
        accountId: data.accountId,
        locationId: data.locationId,
        fee: data.fee,
      } as IProsegurPay;
    } else {
      return this.keepInformationProsegurPayData();
    }
  }

  keepInformationProsegurPayData(): IProsegurPay {
    if (this.detail?.prosegurPay) {
      return {
        accountId: this.detail.prosegurPay.accountId,
        locationId: this.detail.prosegurPay.locationId,
        fee: this.detail.prosegurPay.fee,
      } as IProsegurPay;
    } else {
      return {
        accountId: "",
        locationId: "",
        fee: 0,
      } as IProsegurPay;
    }
  }

  async mounted(): Promise<void> {
    extend("min", {
      validate(value, { length }) {
        return value.length >= length;
      },
      params: ["length"],
      message: i18n.t("validation.messages.min").toString(),
    });

    this.initializeFormData();

    await this.searchChannels();
  }

  debouncedSearchChannels = debounce(async (text?: string) => this.searchChannels(text), 500);

  async searchChannels(text?: string): Promise<void> {
    let countryId = this.detail.subclient?.client?.country?.id;
    const res = await this.fetchChannelsPage({
      pageRequest: {
        page: 0,
        size: 10,
        sort: "text,ASC",
      },
      text,
      countryId,
    });

    this.channels = res?.content.map((data) => new Channel(data)) || [];
  }

  debouncedCreateChannel = debounce(async (text: string) => this.createChannelProcess(text), 1000);

  async createChannelProcess(text: string): Promise<void> {
    const res = await this.createChannel({
      text: text,
      countryId: this.detail.subclient?.client?.country?.id,
    });

    if (res?.status === 1) {
      this.makeToast("success", this.$t(res.message) as string);

      await this.searchChannels();
      this.channelsSelect.push(res.details);
    } else if (res?.status === 0) {
      this.makeToast("danger", this.$t(res.message) as string);

      await this.searchChannels();
    }
  }

  toggleCheckbox(): void {
    if (!this.typeSOL) {
      this.checkboxText = i18n.t("label.smartcash.upper");
    } else {
      this.checkboxText = i18n.t("label.sol.upper");
      this.deviceIdInput = null;
      this.idSmartcashInput = null;
      this.openChannelInput = null;
      this.accountDateInput = null;
      this.currenciesSelect = [];
      this.channelsSelect = [];
      this.ssIdInput = null;
      this.passwordInput = null;
    }

    this.adaptersSelect = [];

    this.checkAdapters();
  }

  checkAdapters(): void {
    this.assignableAdaptersFinal = this.assignableAdapters.filter((adapter) => {
      return this.filterAdapter(adapter);
    });
  }

  filterAdapter(adapter: IAdapter): boolean | undefined {
    if (this.typeSOL) {
      return adapter.name?.includes("Sol Customer");
    } else {
      return adapter.name?.includes("Smartcash") || adapter.name?.includes("Prosegur Pay");
    }
  }

  adaptersSelected(selectedOption: Adapter): void {
    if (selectedOption.code === "deposit") {
      if (this.mainCurrency) {
        const resultMainCurrency = this.currenciesPage?.content.find(
          (e) => e.code === this.mainCurrency
        );
        if (resultMainCurrency) {
          this.currenciesSelect = [resultMainCurrency];
        }
      }
    }
    if (selectedOption.code === "rv") {
      this.ssIdInput = "Cash Today Fit";
      this.passwordInput = "ProsegurC@sh1000";
    }
  }

  adaptersRemoved(removedOption: Adapter): void {
    if (removedOption.code === "deposit") {
      this.currenciesSelect = [];
    }
    if (removedOption.code === "rv") {
      this.ssIdInput = null;
      this.passwordInput = null;
    }
    if (removedOption.code === "pickup") {
      this.agilePickup = false;
    }
  }

  async toggleUnSelectCurrency(currency: ICurrency): Promise<void> {
    if (currency.code === this.mainCurrency) {
      this.$nextTick(() => {
        this.currenciesSelect.unshift(currency);
      });
    }
  }

  async synchronize(): Promise<void> {
    const subclient = this.detail?.subclient;
    const client = subclient?.client;
    const country = this.detail?.subclient?.client?.country;
    if (!subclient && !client && !country) {
      this.makeToastError();
      return;
    }

    const res = await this.$services.servicepoint.syncServicepoint(this.detail.id!);

    if (!res) {
      this.makeToastError();
      return;
    }

    if (!res?.success) {
      this.makeToastErrorWithDescription(res?.message || "unexpectedError.description");
      if (this.detail.active) {
        await this.showModalErrorOnSync();
      }
      return;
    }

    this.synchronizeServicepoint(res?.data as IServicepointDetailData);

    this.$emit("fetch-servicepoint-required");

    this.makeToast("success", this.$t("validation.messages.servicepoint.updated"));
  }

  synchronizeServicepoint(servicepoint: IServicepointDetailData): void {
    this.oidSolInput = servicepoint?.oidSol;
    this.codeSolInput = servicepoint?.codeSol;
    this.descriptionInput = servicepoint?.description;
    this.addressInput = servicepoint?.address;
    this.thirdCodes = servicepoint?.thirdcodes || [];
  }

  makeToastError(): void {
    this.makeToast("danger", this.$t("unexpectedError.description"));
  }

  async showModalErrorOnSync(): Promise<void> {
    const title = this.$t("validation.messages.cannot.synchronize.servicepoint.disclaimer.title");
    const message = this.$t("validation.messages.cannot.synchronize.servicepoint.disclaimer");
    const swal = await this.$swal({
      title: title,
      text: message,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes"),
      cancelButtonText: this.$t("label.no"),
    });
    if (swal.isConfirmed) {
      await this.updateStatusServicepoint(this.detail.id!);
    }
    this.$emit("fetch-servicepoint-required");
  }

  makeToastErrorWithDescription(messageError: string): void {
    this.makeToast("danger", this.$t(messageError));
  }

  onHandleProsegurPayComplete(data: IProsegurPay): void {
    this.prosegurPayInput = data;
  }

  handleAddUserApproval(user: IUserServicepointApproval): void {
    const isDuplicate = this.usersApprovalList.find((existingUser) => existingUser.id === user.id);
    if (!isDuplicate) {
      this.usersApprovalList.push(user);
    }
  }

  handleRemoveUserApproval(user: IUserServicepointApproval): void {
    this.usersApprovalList = this.usersApprovalList.filter(
      (existingUser) => existingUser.id !== user.id
    );
  }
}
